/*
 * product-option-accordion-pricing.js — variant-native delta prices for
 * configurator accordions (single-option + compound-part rows).
 *
 * Replaces the removed `accordion_static_prices` block setting. Prices are
 * always derived from the live variant matrix (embedded JSON, then the
 * paginated ?view=variants load from product-variant-matrix.js).
 *
 * Listens for `variant:update` and `product-variants:loaded` so summary
 * and row deltas stay in sync when any other option changes.
 */
import { formatMoney } from '@theme/money-formatting';

function cssEscape(value) {
  return window.CSS && CSS.escape ? CSS.escape(value) : value;
}

function getOptionValue(variant, position) {
  if (window.ProductVariantMatrix?.optionValue) {
    return window.ProductVariantMatrix.optionValue(variant, position);
  }
  return variant?.['option' + position] ?? null;
}

function readProductJson(form) {
  const node = form.querySelector('.product-purchase-form__product-json');
  if (!node?.textContent) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (_e) {
    return null;
  }
}

function readVariants(form) {
  const json = readProductJson(form);
  return json?.variants || [];
}

function getCurrentVariantId(form) {
  const input = form.querySelector('[data-product-form-variant-id], [name="id"]');
  return input?.value ? String(input.value) : '';
}

function findVariantById(variants, id) {
  if (!id) return null;
  return variants.find((v) => String(v.id) === String(id)) || null;
}

function readSelectedOptions(form) {
  const json = readProductJson(form);
  const count = json?.options?.length || 0;
  const selected = new Array(count).fill('');

  form.querySelectorAll('[data-option-select]').forEach((el) => {
    const pos = parseInt(el.dataset.optionPosition, 10);
    if (!pos) return;
    const idx = pos - 1;
    if (el.tagName === 'SELECT') {
      selected[idx] = el.value;
    } else if (el.type === 'radio' && el.checked) {
      selected[idx] = el.value;
    }
  });

  return selected;
}

function anchorToCurrentVariant(selected, currentVariant, optionCount) {
  const anchored = selected.slice(0, optionCount);
  while (anchored.length < optionCount) anchored.push('');

  for (let i = 0; i < optionCount; i++) {
    if (anchored[i] != null && anchored[i] !== '') continue;
    const val = getOptionValue(currentVariant, i + 1);
    if (val != null && val !== '') anchored[i] = val;
  }

  return anchored;
}

function findVariant(variants, selectedOptions, optionNames) {
  if (!selectedOptions.length) return null;
  if (!selectedOptions.some((v) => v != null && v !== '')) return null;

  const matchesSelection = (variant, skipEmptyVariantOptions) =>
    selectedOptions.every((val, i) => {
      if (val == null || val === '') return true;
      const variantVal = getOptionValue(variant, i + 1);
      if (variantVal == null && skipEmptyVariantOptions) return true;
      return variantVal === val;
    });

  const exact = variants.find((v) => matchesSelection(v, false));
  if (exact) return exact;

  const candidates = variants.filter((v) => matchesSelection(v, true));
  if (!candidates.length) return null;
  if (candidates.length === 1) return candidates[0];

  const names = optionNames || [];
  const compoundIdx = names.findIndex((name) => name && String(name).indexOf(':') !== -1);
  if (compoundIdx >= 0 && selectedOptions[compoundIdx]) {
    const compoundMatches = candidates.filter(
      (v) => getOptionValue(v, compoundIdx + 1) === selectedOptions[compoundIdx]
    );
    if (compoundMatches.length) {
      return compoundMatches.find((v) => v.available) || compoundMatches[0];
    }
  }

  return candidates.find((v) => v.available) || candidates[0];
}

function formatDelta(deltaCents, moneyFormat, currency) {
  const stripZeros = (text) => text.replace(/\.00(?=\D|$)/, '');
  if (deltaCents > 0) {
    return '+' + stripZeros(formatMoney(deltaCents, moneyFormat, currency));
  }
  if (deltaCents < 0) {
    return '-' + stripZeros(formatMoney(Math.abs(deltaCents), moneyFormat, currency));
  }
  return stripZeros(formatMoney(0, moneyFormat, currency));
}

function ensureRowPriceSpan(label) {
  let rowPrice = label.querySelector('.product-option-accordion__row-price');
  if (!rowPrice) {
    rowPrice = document.createElement('span');
    rowPrice.className = 'product-option-accordion__row-price';
    label.appendChild(rowPrice);
  }
  return rowPrice;
}

function paintLabelPrice(label, priceText) {
  label.dataset.optionValuePrice = priceText;
  const rowPrice = label.querySelector('.product-option-accordion__row-price');
  if (rowPrice) rowPrice.textContent = priceText;
  else if (label.closest('.product-option-accordion__body--list')) {
    ensureRowPriceSpan(label).textContent = priceText;
  }
}

function refreshRowSummary(row) {
  const optionName = row.dataset.optionAccordion;
  if (!optionName) return;
  const checked = row.querySelector('.product-option-accordion__input:checked');
  const label = checked?.nextElementSibling;
  const summaryPrice = row.querySelector(
    `[data-option-summary-price="${cssEscape(optionName)}"]`
  );
  if (summaryPrice && label) {
    summaryPrice.textContent = label.dataset.optionValuePrice || '';
  }
}

function updateSingleAccordion(row, variants, currentVariant, selected, moneyFormat, currency, optionNames) {
  const positionInput = row.querySelector('[data-option-position]');
  const position = parseInt(positionInput?.dataset.optionPosition, 10);
  if (!position) return;

  const basePrice = Number(currentVariant?.price || 0);
  const anchored = anchorToCurrentVariant(selected, currentVariant, optionNames.length);

  row.querySelectorAll('[data-option-value-label]').forEach((label) => {
    const input = label.previousElementSibling;
    const value = input?.value;
    if (!value) return;

    const trial = anchored.slice();
    trial[position - 1] = value;
    const variant = findVariant(variants, trial, optionNames);
    const priceText = variant ? formatDelta(Number(variant.price) - basePrice, moneyFormat, currency) : '';
    paintLabelPrice(label, priceText);
  });

  refreshRowSummary(row);
}

function readCompoundPartValues(group) {
  const values = [];
  for (let pn = 1; ; pn += 1) {
    const checked = group.querySelector(`input[data-compound-part="${pn}"]:checked`);
    if (!checked) break;
    values.push(checked.getAttribute('data-compound-value') || '');
  }
  return values;
}

function updateCompoundGroup(group, variants, currentVariant, selected, moneyFormat, currency, optionNames) {
  const sep = group.getAttribute('data-compound-sep') || ' / ';
  const position = parseInt(group.getAttribute('data-compound-position'), 10);
  if (!position) return;

  const basePrice = Number(currentVariant?.price || 0);
  const partValues = readCompoundPartValues(group);
  const anchored = anchorToCurrentVariant(selected, currentVariant, optionNames.length);

  for (let pn = 1; ; pn += 1) {
    const inputs = group.querySelectorAll(`input[data-compound-part="${pn}"]`);
    if (!inputs.length) break;

    inputs.forEach((input) => {
      const value = input.getAttribute('data-compound-value');
      if (!value) return;

      const trialParts = partValues.slice();
      trialParts[pn - 1] = value;
      const combined = trialParts.join(sep);
      const trialSelected = anchored.slice();
      trialSelected[position - 1] = combined;

      const variant = findVariant(variants, trialSelected, optionNames);
      const priceText = variant ? formatDelta(Number(variant.price) - basePrice, moneyFormat, currency) : '';
      const label = input.nextElementSibling;
      if (label) paintLabelPrice(label, priceText);
    });

    const row = group.querySelector(`input[data-compound-part="${pn}"]`)?.closest('[data-option-accordion]');
    if (row) refreshRowSummary(row);
  }
}

function updateAll(root, currentVariant) {
  const scope = root?.querySelector ? root : document;
  const form = scope.querySelector('.product-purchase-form');
  if (!form) return;

  const variants = readVariants(form);
  if (!variants.length) return;

  const variant =
    currentVariant || findVariantById(variants, getCurrentVariantId(form)) || variants[0];
  if (!variant) return;

  const moneyFormat = form.dataset.moneyFormat || '${{amount}}';
  const currency = form.dataset.currency || 'USD';
  const selected = readSelectedOptions(form);
  const productJson = readProductJson(form);
  const optionNames = productJson?.options || [];

  scope.querySelectorAll('[data-option-accordion]').forEach((row) => {
    if (row.closest('[data-compound-accordion]')) return;
    if (row.closest('[data-ring-size-host]')) return;
    updateSingleAccordion(row, variants, variant, selected, moneyFormat, currency, optionNames);
  });

  scope.querySelectorAll('[data-compound-accordion]').forEach((group) => {
    updateCompoundGroup(group, variants, variant, selected, moneyFormat, currency, optionNames);
  });
}

function onVariantUpdate(event) {
  const section = event.target?.closest?.('.shopify-section') || document;
  updateAll(section, event.detail?.resource);
}

function onVariantsLoaded(event) {
  const detail = event.detail || {};
  const root = detail.root || document;
  const id = root.querySelector?.('[data-product-form-variant-id], [name="id"]')?.value;
  const variant = detail.variants?.find((v) => String(v.id) === String(id)) || null;
  updateAll(root, variant);
}

function init(root) {
  updateAll(root || document, null);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('variant:update', onVariantUpdate);
document.addEventListener('product-variants:loaded', onVariantsLoaded);
document.addEventListener('shopify:section:load', (event) => init(event.target));
